import React, { useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Check } from 'lucide-react';
import { mockData } from '../../mock';

const PortfolioCard = ({ project, index, currentImageIndex, nextImage, prevImage, goToImage }) => {
  
  // Auto-play carousel
  useEffect(() => {
    const interval = setInterval(() => {
      nextImage();
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, [nextImage]);

  // Preload next image for smoother transitions
  useEffect(() => {
    if (project.images.length > 1) {
      const nextIndex = (currentImageIndex + 1) % project.images.length;
      const img = new Image();
      img.src = project.images[nextIndex];
    }
  }, [currentImageIndex, project.images]);
  
  return (
    <div className="group">
      <Card 
        className={`bg-gradient-to-br ${
          index === 0 ? 'from-[#1a1a1a] via-[#151515] to-[#111111]' :
          index === 1 ? 'from-[#151515] via-[#121212] to-[#0f0f0f]' :
          index === 2 ? 'from-[#1c1c1c] via-[#181818] to-[#121212]' :
          'from-[#171717] via-[#141414] to-[#101010]'
        } border-gray-800 overflow-hidden hover:border-[#2F6BFF]/50 transition-colors duration-300 h-full flex flex-col shadow-3d-hover`}
        style={{ 
          boxShadow: '0 10px 30px rgba(0,0,0,0.5), 0 20px 60px rgba(0,0,0,0.3), inset 0 0 50px rgba(34,211,238,0.03)'
        }}
      >
        {/* Image Carousel with 3D Perspective */}
        <div className="relative overflow-hidden h-80 group/carousel" style={{ transformStyle: 'preserve-3d' }}>
          {/* Category Badge - Standardized */}
          <div className="absolute top-4 left-4 z-20">
            <Badge className="inline-flex items-center justify-center h-8 px-4 py-1.5 bg-gradient-to-r from-[#2F6BFF] to-[#4DA3FF] text-white text-sm font-medium leading-5 rounded-full shadow-lg hover:shadow-[#2F6BFF]/50 hover:scale-105 transition-all duration-300">
              {project.category}
            </Badge>
          </div>
          
          {/* Main Image with 3D Transform */}
          <div 
            className={`relative w-full h-full flex items-center justify-center ${
              project.id === 4 ? 'bg-white' : 'bg-gradient-to-br from-[#0f0f0f] to-[#0a0a0a]'
            }`}
            style={{ 
              transformStyle: 'preserve-3d',
              transform: `translateZ(20px)`
            }}
          >
            {project.images.map((image, idx) => (
              <img 
                key={idx}
                src={image} 
                alt={`${project.title} - Image ${idx + 1}`}
                className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-500 ease-in-out ${
                  idx === currentImageIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
                loading={idx === 0 ? "eager" : "lazy"}
                style={{
                  filter: 'brightness(0.95) contrast(1.05)',
                }}
              />
            ))}
          </div>
          
          {/* Enhanced Gradient Overlay with Animation */}
          <div 
            className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent transition-opacity duration-700"
            style={{
              opacity: 0.7,
              background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.3) 50%, transparent 100%)'
            }}
          ></div>
          
          {/* Navigation Arrows with Enhanced Styling */}
          {project.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                className="absolute left-3 top-1/2 -translate-y-1/2 z-20 bg-black/70 hover:bg-gradient-to-r from-[#2F6BFF] to-[#4DA3FF] backdrop-blur-sm text-white p-3 rounded-full opacity-0 group-hover/carousel:opacity-100 transition-all duration-300 hover:scale-110 shadow-lg"
                aria-label="Previous image"
                style={{ transform: 'translateZ(40px) translateY(-50%)' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextImage}
                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 bg-black/70 hover:bg-gradient-to-r from-[#2F6BFF] to-[#4DA3FF] backdrop-blur-sm text-white p-3 rounded-full opacity-0 group-hover/carousel:opacity-100 transition-all duration-300 hover:scale-110 shadow-lg"
                aria-label="Next image"
                style={{ transform: 'translateZ(40px) translateY(-50%)' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
          
          {/* Enhanced Navigation Dots */}
          {project.images.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex gap-2 bg-black/50 backdrop-blur-md px-4 py-2 rounded-full">
              {project.images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => goToImage(idx)}
                  className={`transition-all duration-300 rounded-full ${
                    idx === currentImageIndex 
                      ? 'bg-gradient-to-r from-[#2F6BFF] to-[#4DA3FF] w-10 h-3 shadow-lg shadow-[#2F6BFF]/50' 
                      : 'bg-white/40 hover:bg-white/70 w-3 h-3'
                  }`}
                  aria-label={`Go to image ${idx + 1}`}
                  style={{ transform: 'translateZ(30px)' }}
                />
              ))}
            </div>
          )}
          
          {/* Enhanced Image Counter - Standardized */}
          <div 
            className="absolute top-4 right-5 z-20 inline-flex items-center justify-center h-8 px-4 py-1.5 bg-black/70 backdrop-blur-md rounded-full text-sm font-medium text-white shadow-lg border border-[#2F6BFF]/20"
            style={{ transform: 'translateZ(30px)' }}
          >
            <span className="text-[#2F6BFF] font-semibold">{currentImageIndex + 1}</span>
            <span className="text-[#9AA3B2] mx-1.5">/</span>
            <span className="font-semibold">{project.images.length}</span>
          </div>
        </div>
        
        {/* Content with 3D Depth */}
        <CardHeader 
          className="flex-grow space-y-4 p-6"
          style={{ 
            transformStyle: 'preserve-3d',
            transform: 'translateZ(30px)'
          }}
        >
          <CardTitle className="text-2xl text-[#F5F7FF] group-hover:text-[#2F6BFF] transition-colors duration-300">
            {project.title}
          </CardTitle>
          <CardDescription className="text-[#9AA3B2] leading-relaxed">
            {project.description}
          </CardDescription>
          {mockData.portfolioFeatures[project.id] && (
            <div className="pt-4 border-t border-gray-800/50">
              <ul className="space-y-2">
                {mockData.portfolioFeatures[project.id].map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-gray-500 group-hover:text-[#9AA3B2] transition-colors">
                    <Check className="h-4 w-4 text-[#2F6BFF] flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardHeader>
      </Card>
    </div>
  );
};

export default PortfolioCard;
